import FilterSelect from '@Components/Filter/Dropdown';
import Image from '@Components/Image';
import { RootStateType, AppDispatch } from '@Store';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { DropdownOptionType } from '@Components/Dropdown/Type';

import API from '@Services/apiAxios';
import { getLoggedInUserSystemList } from '../../store/reducers/Systems';

import CustomTabs from '@Components/AdminHome/CustomTabs';
import ExportImportBarChart from './ExportImportBarChart';
import WeightChart from './WeightBarChart';
import ShipmentsChart from './ShipmentsChart';

interface MonthlyData {
  totalWeight: number;
  totalShipment: number;
  exportShipments: number;
  importShipments: number;
  month: string;
  year: string;
}

interface MonthlyStatsResponse {
  totalShipment: number;
  totalWeight: number;
  shipmentPercentage: number;
  weightPercentage: number;
  monthlyData: MonthlyData[];
}

const generateMonthOptions = (): DropdownOptionType[] => {
  return Array.from({ length: 6 }, (_, i) => {
    const date = moment().subtract(i, 'months');
    return {
      value: date.format('MM-YYYY'), // Month-Year format
      display: date.format('MMMM YYYY'), // Full month name with year
    };
  });
};

const monthOptions = generateMonthOptions();

interface StatCardProps {
  icon: string;
  alt: string;
  value: string | number;
  percentage: number;
  title: string;
  description: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, alt, value, percentage, title, description }) => {
  const isPositive = percentage >= 0;

  return (
    <Box
      sx={{
        padding: '24px',
        backgroundColor: 'background.paper',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.2s ease-in-out',
        height: '100%',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: '60px',
            height: '60px',
            borderRadius: '10px',
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Image src={icon} alt={alt} style={{ width: '36px', height: '34px' }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              padding: '4px 8px',
              borderRadius: '6px',
              backgroundColor: isPositive ? 'success.light' : 'error.light',
              mb: 1.5,
            }}
          >
            <Image
              src={isPositive ? '/images/icons/AdminHome/upArrow.svg' : '/images/icons/AdminHome/downArrow.svg'}
              alt="trend"
              style={{ width: '10px', height: '12px' }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: isPositive ? 'success.main' : 'error.main',
                fontSize: '12px',
              }}
            >
              {percentage}%
            </Typography>
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '24px', md: '28px' },
              lineHeight: 1.2,
              color: 'text.primary',
              mb: 0.5,
              fontFeatureSettings: '"tnum" on, "lnum" on',
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '13px',
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const UserGeneralOverview = () => {
  const [system, setSystem] = useState<string[]>([]);
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  const [selectedValues, setSelectedValues] = useState<{ month: string; year: string }>({
    month: moment().format('MM'),
    year: moment().format('YYYY'),
  });
  const dispatch = useDispatch<AppDispatch>();
  const [stats, setStats] = useState<MonthlyStatsResponse | null>(null);

  const tabLabels = ['Type', 'Weight', 'Shipment'];

  const getMonthlyStats = async () => {
    try {
      const response = await API.get('/analytics/shipment/user/monthly', {
        params: {
          month: selectedValues.month,
          year: selectedValues.year,
          'system[]': system,
        },
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
      throw error;
    }
  };

  useEffect(() => {
    getMonthlyStats();
  }, [selectedValues, system]);

  useEffect(() => {
    dispatch(getLoggedInUserSystemList());
  }, []);

  const tabContent = {
    Type: (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Shipment Card */}
          <Grid item xs={12} md={6}>
            <StatCard
              icon="/images/icons/AdminHome/shipment.png"
              alt="shipment"
              value={stats?.totalShipment || '--'}
              percentage={stats?.shipmentPercentage || 0}
              title="Shipment"
              description="Total shipments"
            />
          </Grid>

          {/* Weight Card */}
          <Grid item xs={12} md={6}>
            <StatCard
              icon="/images/icons/AdminHome/weight.png"
              alt="weight"
              value={stats?.totalWeight || '--'}
              percentage={stats?.weightPercentage || 0}
              title="Total Weight"
              description="Total Weight (KG)"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>{stats?.monthlyData && <ExportImportBarChart data={stats.monthlyData} />}</Box>
      </Box>
    ),

    Weight: (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Shipment Card */}
          <Grid item xs={12} md={6}>
            <StatCard
              icon="/images/icons/AdminHome/shipment.png"
              alt="shipment"
              value={stats?.totalShipment || '--'}
              percentage={stats?.shipmentPercentage || 0}
              title="Shipment"
              description="Total shipments"
            />
          </Grid>

          {/* Weight Card */}
          <Grid item xs={12} md={6}>
            <StatCard
              icon="/images/icons/AdminHome/weight.png"
              alt="weight"
              value={stats?.totalWeight || '--'}
              percentage={stats?.weightPercentage || 0}
              title="Total Weight"
              description="Total Weight (KG)"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>{stats?.monthlyData && <WeightChart data={stats.monthlyData} />}</Box>
      </Box>
    ),

    Shipment: (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Shipment Card */}
          <Grid item xs={12} md={6}>
            <StatCard
              icon="/images/icons/AdminHome/shipment.png"
              alt="shipment"
              value={stats?.totalShipment || '--'}
              percentage={stats?.shipmentPercentage || 0}
              title="Shipment"
              description="Total shipments"
            />
          </Grid>

          {/* Weight Card */}
          <Grid item xs={12} md={6}>
            <StatCard
              icon="/images/icons/AdminHome/weight.png"
              alt="weight"
              value={stats?.totalWeight || '--'}
              percentage={stats?.weightPercentage || 0}
              title="Total Weight"
              description="Total Weight (KG)"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>{stats?.monthlyData && <ShipmentsChart data={stats.monthlyData} />}</Box>
      </Box>
    ),
  };

  const handleChange = (event: SelectChangeEvent<`${string}-${string}`>) => {
    const [month, year] = event.target.value.split('-'); // Extract month and year
    setSelectedValues({ month, year });
  };

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
  };
  return (
    <Box
      sx={{
        borderRadius: '9px',
        border: '1px solid #F1F1F1',
        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 40px',
      }}
    >
      <Box sx={{ color: 'rgba(24, 29, 37, 1)', fontSize: '24px', fontWeight: 600, lineHeight: '33.6px' }}>
        General Overview
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row-reverse' },
            gap: '6px',
            height: 'fit-content',
            position: 'relative',
            top: '45px',
          }}
        >
          {/* <Box>
            <DownloadButton
              name={'Download'}
              onClick={() => {
                console.info('click');
              }}
            />
          </Box> */}
          <Box>
            {/* <FilterSelect
              id="month"
              label="Select month"
              options={monthOptions}
              value={selectedValues}
              onChange={handleChange}
            /> */}
            <FormControl fullWidth>
              <InputLabel id="month-select-label">Select month</InputLabel>
              <Select
                labelId="month-select-label"
                id="month-select"
                value={`${selectedValues.month}-${selectedValues.year}`} // Set the selected value
                onChange={handleChange}
                label="Select month"
                sx={{ borderRadius: '12px' }}
              >
                {monthOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FilterSelect
              id="system"
              label="Select system"
              options={loggedInUserSystemDropdownList}
              value={system}
              onChange={onSystemFilter}
            />
          </Box>
        </Box>
        <Box sx={{ marginTop: { xs: '70px', sm: '0px' }, marginBottom: { xs: '70px', sm: '0px' } }}>
          <CustomTabs tabs={tabLabels} tabContent={tabContent} />
        </Box>
      </Box>
    </Box>
  );
};

export default UserGeneralOverview;
